import { useCallback } from 'react';
import { FieldArray, getIn, useFormikContext } from 'formik';
import type { FieldArrayRenderProps } from 'formik';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { EMPTY_STRING } from '@e-socna/empty';

import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { getTouchedError } from '../../../../utils/get-touched-error.util';
import { generateId } from '../../../../utils/generate-id.util';

import type { ActorForm_Actor } from '../../fragments/actor.fragment';

const FIELD_NAME = 'references';
const removeLabel = 'Supprimer la référence';

// The references an external actor gives the entity: 0, 1 or N, each typed (« Référence » by
// default) with its value.
export const ExternalReferences = () => {
  const { errors, handleBlur, handleChange, touched, values } =
    useFormikContext<ActorForm_Actor>();
  const { defaultExternalReferenceType, externalReferenceTypes } =
    useReferencesContext();

  const { references } = values;

  const render = useCallback(
    ({ push, remove }: Readonly<FieldArrayRenderProps>) => (
      <Stack spacing={2}>
        {references.map((reference, index) => {
          const name = `${FIELD_NAME}[${index.toFixed()}]`;

          return (
            <Grid container key={reference._id} spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  id={`actor-details-references-${index.toFixed()}-type`}
                  label="Type"
                  name={`${name}.typeId`}
                  onChange={handleChange}
                  select
                  value={reference.typeId}
                >
                  {externalReferenceTypes.map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 10, md: 7 }}>
                <TextField
                  error={
                    !!(
                      getIn(errors, `${name}.value`) &&
                      getIn(touched, `${name}.value`)
                    )
                  }
                  fullWidth
                  helperText={getTouchedError(errors, touched, `${name}.value`)}
                  id={`actor-details-references-${index.toFixed()}-value`}
                  label="Valeur"
                  name={`${name}.value`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  required
                  value={reference.value}
                />
              </Grid>
              <Grid size={{ xs: 2, md: 1 }} sx={{ display: 'flex' }}>
                <Tooltip title={removeLabel}>
                  <IconButton
                    aria-label={removeLabel}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => {
                      remove(index);
                    }}
                    sx={{ alignSelf: 'center' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          );
        })}
        <Button
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => {
            push({
              _id: generateId(),
              typeId: defaultExternalReferenceType?._id ?? EMPTY_STRING,
              value: EMPTY_STRING,
            });
          }}
          startIcon={<AddIcon />}
          sx={{ alignSelf: 'flex-start' }}
          variant="outlined"
        >
          Ajouter une référence
        </Button>
      </Stack>
    ),
    [
      defaultExternalReferenceType?._id,
      errors,
      externalReferenceTypes,
      handleBlur,
      handleChange,
      references,
      touched,
    ],
  );

  return (
    <Stack spacing={2} sx={{ m: 3 }}>
      <FormLabel component="legend">Références</FormLabel>
      <FieldArray name={FIELD_NAME} render={render} />
    </Stack>
  );
};
